/**
 * Session-bound store for MAUI (remix) editable-text values, keyed by embed and
 * version. Part of the MAUI reload-free undo/redo (gate
 * `platform_editor_maui_remix_app_id`).
 *
 * Why this lives in `native-embeds-common` rather than the editor plugin: the
 * store is *managed by* the native-embeds editor plugin (it drives every write,
 * from the `native-embed-new-version` handler), but the value must also be
 * *read* by `native-embeds-editor-extension` when it renders the embed, to feed
 * the reload-free apply down to the child. The plugin already depends on the
 * extension, so a store physically inside the plugin package could not be read
 * by the extension without a dependency cycle. `native-embeds-common` is the
 * cycle-free package both sides already depend on.
 *
 * The map is a plain module-level singleton: session-lifetime, never serialized
 * to ADF, and intentionally not part of any ProseMirror plugin state (so it
 * carries no undo/redo baggage of its own — undo/redo of the *version pointer*
 * lives in editor history; this map only answers "what were the slot values at
 * version X?").
 */

export interface RemixSlotValue {
	slot: string;
	value: string;
}

/**
 * `localId → (versionId → { slot: value })`.
 *
 * Each version holds a **full snapshot** of the edited slots as of that version
 * (built incrementally from single-slot edits), so applying `snapshot[target]`
 * on undo/redo fully restores that version's text. Slots that have never been
 * edited are absent and simply left untouched in the app (they never change).
 */
const slotValuesByEmbed = new Map<string, Map<string, Record<string, string>>>();

const getEmbedMap = (localId: string): Map<string, Record<string, string>> => {
	let perEmbed = slotValuesByEmbed.get(localId);
	if (!perEmbed) {
		perEmbed = new Map<string, Record<string, string>>();
		slotValuesByEmbed.set(localId, perEmbed);
	}
	return perEmbed;
};

/**
 * Record the single edit that produced a new version, extending the incremental
 * per-version snapshots.
 *
 * - `prevVersionId` snapshot is seeded with the slot's `previousValue` (once, if
 *   not already known) so that undoing back to it restores the pre-edit text.
 * - `newVersionId` snapshot = the previous snapshot with the edited slot set to
 *   its new `value`.
 *
 * All fields except `newVersionId` are best-effort: with no `slot`/`value` the
 * new version simply inherits the previous snapshot; with no `prevVersionId`
 * (e.g. the embed had no stored version yet) the base snapshot is not seeded.
 */
export const recordRemixSlotValues = ({
	localId,
	prevVersionId,
	newVersionId,
	slot,
	previousValue,
	value,
}: {
	localId: string;
	newVersionId: string;
	previousValue?: string;
	prevVersionId?: string;
	slot?: string;
	value?: string;
}): void => {
	if (!localId || !newVersionId) {
		return;
	}
	const perEmbed = getEmbedMap(localId);

	let prevSnapshot = (prevVersionId && perEmbed.get(prevVersionId)) || {};
	if (prevVersionId && slot && previousValue !== undefined && !(slot in prevSnapshot)) {
		// Seed the pre-edit value on the previous version once, so undo restores it.
		prevSnapshot = { ...prevSnapshot, [slot]: previousValue };
		perEmbed.set(prevVersionId, prevSnapshot);
	}

	const newSnapshot =
		slot && value !== undefined ? { ...prevSnapshot, [slot]: value } : { ...prevSnapshot };
	perEmbed.set(newVersionId, newSnapshot);
};

/**
 * Return the full slot snapshot for a version as an array, or `undefined` when
 * nothing is recorded for that embed/version (e.g. the base version before any
 * edit, or a version created in another session).
 */
export const getRemixSlotValues = (
	localId: string | undefined,
	versionId: string | undefined,
): RemixSlotValue[] | undefined => {
	if (!localId || !versionId) {
		return undefined;
	}
	const snapshot = slotValuesByEmbed.get(localId)?.get(versionId);
	if (!snapshot) {
		return undefined;
	}
	return Object.entries(snapshot).map(([slot, value]) => ({ slot, value }));
};

/** Test-only: clear the whole store. */
export const resetRemixSlotStore = (): void => {
	slotValuesByEmbed.clear();
};
